import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '~components';
import Modal from '~components/Modal';
import SelectLazy from '~components/form-fields/inputs/SelectLazy';

import { getAll as getClients } from '~main/actions/clients';
import { selectLookupClients } from '~main/selectors/clients/clients';

import { GocardlessMandate } from '~direct-debits/models';

interface ComponentProps {
  mandate: GocardlessMandate | null;
  isOpen: boolean;
  isSubmitting?: boolean;
  onCancel: () => void;
  onConfirm: (clientId: number) => void;
}

const MandateLinkModal: React.FC<ComponentProps> = ({
  mandate,
  isOpen,
  isSubmitting = false,
  onCancel,
  onConfirm,
}) => {
  // A mandate the API could not attribute automatically may arrive with a name-matched client it
  // proposes. That is a proposal, not a link: it only pre-selects the field, and the operator
  // either confirms it with Link or picks someone else from the same client search list.
  const suggestion = mandate?.suggested_match ?? null;
  const [clientId, setClientId] = useState<number | null>(null);

  // Reset to the suggestion (or to empty when there isn't one) whenever the modal is reopened for
  // a different mandate, so a previous mandate's choice never carries over.
  useEffect(() => {
    if (isOpen) setClientId(suggestion?.client_id ?? null);
  }, [isOpen, mandate?.id, suggestion?.client_id]);

  // SelectLazy loads its options lazily on focus, so a pre-selected client has no option to
  // resolve against until then. `initialValue` gives it the label to show in the meantime.
  const suggestedOption = useMemo(
    () =>
      suggestion ? { value: suggestion.client_id, label: suggestion.client_name ?? 'Suggested client' } : undefined,
    [suggestion],
  );
  const isSuggestionSelected = suggestion !== null && clientId === suggestion.client_id;

  const handleClientChange = (_id: string, value: number | null) => setClientId(value);
  const handleConfirm = () => {
    if (clientId === null) return;
    onConfirm(clientId);
  };

  const setIsOpen = (open: boolean) => {
    if (!open) onCancel();
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      header="Link Mandate"
      includeFooter={false}
      backdrop="static"
    >
      <div className="d-flex flex-column gap-2">
        <div>
          <strong>Customer:</strong> {mandate?.customer_name ?? '-'}
        </div>
        <div>
          <strong>Reference:</strong> {mandate?.gocardless_reference ?? '-'}
        </div>
        {isSuggestionSelected && (
          <div className="alert alert-info mb-0 py-2 mt-2" role="note" data-cy="dd-mandate-link-suggestion">
            <strong>Suggested match, not yet linked.</strong>{' '}
            <strong>{suggestion?.client_name ?? 'This client'}</strong> is a close match on the customer name held
            against this mandate. Confirm it with Link, or choose a different client below.
          </div>
        )}
        <label htmlFor="dd-mandate-link-client" className="mt-2 fw-semibold">
          TurnPoint client
        </label>
        <SelectLazy
          type="select-lazy"
          id="dd-mandate-link-client"
          name="client_id"
          value={clientId ?? undefined}
          initialValue={suggestedOption}
          placeholder="Search clients…"
          selectorOptions={selectLookupClients}
          selectorActions={getClients}
          manualReload
          onChange={handleClientChange}
        />
        <div className="d-flex justify-content-end gap-2 mt-3">
          <Button
            id="dd-mandate-link-cancel"
            data-cy="dd-mandate-link-cancel"
            color="secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            id="dd-mandate-link-confirm"
            data-cy="dd-mandate-link-confirm"
            color="success"
            onClick={handleConfirm}
            disabled={clientId === null || isSubmitting}
          >
            Link
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default MandateLinkModal;
